<template>
  <div class="ly_appSize un_phoneView_wrapper __bgWhite hp_posiRel hp_mH_vh">
    <h1 class="blind">모임 리스트 페이지</h1>

    <!-- 탑 바 -->
    <header class="bl_topBar">
      <NuxtLink class="bl_topBar_logo" to="/"></NuxtLink>
      <div class="bl_topBar_nav">
        <NuxtLink class="el_plainBtn_s" to="/introduceOE">앱 소개</NuxtLink>
        <NuxtLink class="el_plainBtn_s" to="/gathering/feed/list"
          >소통 공간</NuxtLink
        >
      </div>
    </header>

    <h2 class="el_pageTitle">{{ pageTitle }}</h2>
    <div class="ly_reactiveList">
      <NuxtLink
        class="un_gatheringCard"
        v-for="(gathering, index) in getList"
        :key="index"
        :to="`/gathering/${gathering.id}`"
        title="모임 상세 페이지로 이동"
      >
        <CustomImage :src="gathering.image" alt="모임 이미지"></CustomImage>
        <!-- <img :src="gathering.image" v-auto-title alt="모임 이미지" /> -->
        <div class="un_gatheringCard_desc">
          <h4>{{ gathering.interest }}</h4>
          <h3>{{ gathering.title }}</h3>
          <p>
            {{ gathering.description }}
          </p>
          <i class="un_gatheringCard_ps"
            ><strong>{{ gathering.memberInfo.count }}</strong
            >/{{ gathering.memberInfo.capacity }}명</i
          >
        </div>
      </NuxtLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import type GatheringPublic from "./gathering-public.dto";
import api from "../../utils/api";
import { useGatheringListStore } from "~/stores/gathering-list";
import { useRandomSeed } from "../../composable/useRandomSeed";

const query = useRoute().query;
const store = useGatheringListStore();
const { getList, fetchedAll, offset } = storeToRefs(store);
const gatheringList = ref<GatheringPublic[]>(getList.value);
const fetching = ref(false);
const interest = ref(
  query.keyword === "에너지"
    ? ["골프", "등산", "자전거", "레저", "걷기/러닝/마라톤"]
    : query.keyword === "소통"
    ? ["여행", "사교/인맥", "노래/보컬", "사진/영상"]
    : []
);
const limit = 20;

const fetch = async () => {
  if (fetchedAll.value) {
    return;
  }
  if (fetching.value) {
    return;
  }
  fetching.value = true;
  return api
    .getGatheringList(
      interest.value,
      limit,
      offset.value,
      useRandomSeed().value
    )
    .then(({ list, done, nextOffset }) => {
      store.add(list);
      store.setFetchedAll(done);
      if (typeof nextOffset === "number") {
        store.setOffset(nextOffset);
      }
      const page = parseInt((useRoute().query.page as string) || "0") + 1;
      useRouter().replace({ query: { ...useRoute().query, page } });
    })
    .catch(console.error)
    .finally(() => {
      fetching.value = false;
    });
};

await useAsyncData(async () => {
  if (gatheringList.value.length === 0) {
    await fetch();
  }
  return gatheringList.value;
});

useHead({
  title: "오이 취미모임 | 따분한 일상이 바뀌는 순간",
  link: [{ rel: "canonical", href: "https://oe-app.com/gathering/list" }],
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          organizationSchema,
          softwareAppSchema,
          gatheringItemListSchema(
            getList.value.map((gathering) => ({
              id: gathering.id,
              name: gathering.title,
              image: gathering.image,
              description: gathering.description,
            }))
          ),
        ],
      }),
    },
  ],
});

const pageTitle = ref(
  query.keyword === "에너지"
    ? "에너지가 넘치는 모임"
    : query.keyword === "소통"
    ? "소통이 활발한 모임"
    : "취미 모임"
);

let removeBeforeEach: () => void;

onMounted(() => {
  window.addEventListener("scroll", handleScrollEvent);

  removeBeforeEach = useRouter().beforeEach((to, from) => {
    if (to.name !== "gathering-id" && to.path !== from.path) {
      store.clear();
    }
  });
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScrollEvent);
  removeBeforeEach && removeBeforeEach();
});

const handleScrollEvent = (e: Event) => {
  const scrollTop = window.scrollY;
  const viewportHeight = window.innerHeight;
  const fullHeight = document.documentElement.scrollHeight;

  if (scrollTop + viewportHeight >= fullHeight - 100) {
    fetch();
  }
};

const goToGathering = (id: number) => {
  useRouter().push(`/gathering/${id}`);
};

const goTo = (path: string) => {
  useRouter().push(path);
};

const replaceTo = (path: string) => {
  useRouter().replace(path);
};
</script>

<style scoped>
@import "@/assets/css/base.css";
@import "@/assets/css/seo.css";
</style>
